import { Orientation } from '../OrientationManager';
import { NumberFormatMode } from '../Toolkit/Components/Label/NumberFormatter';
import { BroswerCheckOptions } from '../Toolkit/Utils/BrowserChecker';

/**
 * config which should be shared by all games
 */
export type SharedEnvironmentConfigType = {
    gameDomain: string;
    bonusDomain: string;
    token: string;
    connectionTimeoutDuration: number;
    isFakeToken: boolean;
    language: string;
    homeURL: string;
    platformID: number;
    walletType: number;
    currency: string;
    needEncrypt: boolean;
    debug: boolean;
    showFPS: boolean;
    useIFrame: boolean;
    /**
     * 是否允許在電腦上旋轉螢幕
     */
    allowRotationOnPC: boolean;
    hideList: string;
    hideTxID: boolean;
    hideExit: boolean;
    hideZero: boolean;
    hideCurrency: boolean;
    hideVersion: boolean;
    compressErrorStack: boolean;
    debugKey: boolean;
    enableFullScreen: boolean;
    /** 是否支援CRM */
    supportCRM: boolean;
    crm: boolean;
    crmBundleURL: string;
    useInternalCRMBundle?: boolean;
    waitForCRM?: boolean;
    numberFormatterMode: NumberFormatMode;
    displayTimeZone?: boolean;
    forceMaxDecimalDigits?: number;
    checkBrowser?: BroswerCheckOptions;
    imageQuality: number; // 畫質(指定 maxWindowSideLength)
    timezoneOffset?: string; // 相對UTC的時區偏移量，格式為"+08:00"
};

export type OrientationConfigType = {
    /**
     * 預設的螢幕方向'
     */
    default: Orientation;
    /**
     * 根據螢幕轉向決定預設的螢幕方向
     */
    autoDefault?: boolean;
    /**
     * 是否支援旋轉螢幕
     */
    supportRotation: boolean;
    /**
     * 是否禁止自動旋轉螢幕
     */
    forbidRotation?: boolean;
    /**
     * 是否允許手動旋轉螢幕
     */
    allowManualRotation?: boolean;
    orientationScale?: number;
};

// for backward compatibility
export type { SharedEnvironmentConfigType as SeriesEnvironmentConfigType };

/**
 * config which should be defined by individual game
 */
export type IndividualEnvironmentConfigType = {
    gameID: string;
    gameVersion: string;
    orientationConfig: OrientationConfigType;
};

// for backward compatibility
export type { IndividualEnvironmentConfigType as FeatureEnvironmentConfigType };

type EnvironmentConfigType = SharedEnvironmentConfigType & IndividualEnvironmentConfigType;

export type ConfigParamsMap = {
    [config in keyof EnvironmentConfigType]: string;
} & { hideList: string; forbidRotation: string; allowManualRotation: string; defaultOrientation: string; autoDefaultOrientation: string };

export const defaultConfigParamsMap: ConfigParamsMap = {
    gameID: 'gameID',
    gameDomain: 'gameDomain',
    bonusDomain: 'bonusDomain',
    token: 'token',
    connectionTimeoutDuration: 'connectionTimeoutDuration',
    platformID: 'platform',
    walletType: 'wt',
    currency: 'cur',
    language: 'lang',
    homeURL: 'homeUrl',
    showFPS: 'hidefps',
    useIFrame: 'useIFrame',
    allowRotationOnPC: 'allowRotation',
    forbidRotation: 'forbidRotation',
    allowManualRotation: 'allowManualRotation',
    defaultOrientation: 'defaultOrientation',
    autoDefaultOrientation: 'autoDefaultOrientation',
    hideTxID: 'txID',
    hideExit: 'exit',
    hideZero: 'zero',
    hideCurrency: 'curr',
    hideVersion: 'version',
    compressErrorStack: 'ces',
    debugKey: 'debugKey',
    enableFullScreen: 'fullscreen',
    hideList: 'hide',
    crm: 'crm',
    supportCRM: 'supportCRM',
    crmBundleURL: 'crmBundleURL',
    waitForCRM: 'waitForCRM',
    numberFormatterMode: 'nf',
    forceMaxDecimalDigits: 'decimalDigits',
    displayTimeZone: 'withTZ',
    imageQuality: 'quality',
    timezoneOffset: 'timezone',

    // config which should not read from url
    gameVersion: null,
    isFakeToken: null,
    needEncrypt: null,
    debug: null,
    orientationConfig: null,
    checkBrowser: null,
};

export default EnvironmentConfigType;
